import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { 
    Phone, Share2, Trash2, Edit, ChevronLeft, 
    Plus, Save, AlignLeft, Link as LinkIcon
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

const ManagerContainer = styled.div` width: 100%; `;
const HeaderRow = styled.div` display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; h2 { margin: 0; font-size: 1.8rem; color: #111; font-weight: 800; display: flex; align-items: center; gap: 10px; } `;
const PrimaryButton = styled.button` display: flex; align-items: center; gap: 8px; background: #111; color: #fff; padding: 10px 18px; border: none; border-radius: 8px; font-weight: 700; cursor: pointer; transition: all 0.3s; &:hover { background: #333; transform: translateY(-2px); } &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; } `;
const SecondaryButton = styled.button` display: flex; align-items: center; gap: 8px; background: #fff; color: #111; padding: 10px 18px; border: 1px solid #ddd; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.3s; &:hover { background: #f4f6f8; } `;

const FormCard = styled.form` background: #fff; border-radius: 12px; padding: 30px; border: 1px solid #eee; box-shadow: 0 4px 15px rgba(0,0,0,0.03); margin-bottom: 30px; `;
const FormSectionTitle = styled.h4` margin: 0 0 20px 0; font-size: 1.1rem; color: #111; border-bottom: 2px solid #f0f0f0; padding-bottom: 10px; display: flex; align-items: center; gap: 10px; svg { color: #888; } `;
const FormGroup = styled.div` display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; label { font-size: 0.9rem; font-weight: 700; color: #555; } input { padding: 12px 14px; border: 1px solid #ddd; border-radius: 8px; font-size: 1rem; font-family: inherit; transition: border-color 0.3s; &:focus { outline: none; border-color: #111; } } `;
const FormGroupGrid = styled.div` display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; margin-bottom: 20px; `;

const Grid = styled.div` display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; margin-bottom: 50px; `;
const ContactCard = styled.div` background: #fff; border-radius: 12px; border: 1px solid #eee; box-shadow: 0 4px 15px rgba(0,0,0,0.03); display: flex; flex-direction: column; overflow: hidden; transition: transform 0.3s ease; &:hover { transform: translateY(-3px); box-shadow: 0 8px 25px rgba(0,0,0,0.06); } .content { padding: 25px; display: flex; align-items: center; gap: 15px; flex: 1; .icon-circle { width: 50px; height: 50px; background: #f4f6f8; border-radius: 50%; display: flex; align-items: center; justify-content: center; img { width: 25px; height: 25px; object-fit: contain; filter: grayscale(100%); } svg { color: #888; } } .info { display: flex; flex-direction: column; gap: 5px; h3 { margin: 0; font-size: 1rem; color: #888; text-transform: uppercase; letter-spacing: 1px; } p { margin: 0; font-size: 1.1rem; font-weight: 800; color: #111; word-break: break-all; } span { font-size: 0.85rem; color: #234ffc; font-weight: 600; } } } .actions { display: flex; border-top: 1px solid #f0f0f0; background: #fafafa; button { flex: 1; display: flex; justify-content: center; align-items: center; gap: 5px; padding: 15px; border: none; font-weight: 600; cursor: pointer; transition: 0.2s; background: transparent; } .btn-edit { color: #234ffc; border-right: 1px solid #f0f0f0; } .btn-edit:hover { background: #eef2ff; } .btn-delete { color: #dc3545; } .btn-delete:hover { background: #fff0f0; } } `;

export default function ContactManager() {
    const [contacts, setContacts] = useState([]);
    const [socials, setSocials] = useState([]);
    
    const [searchParams, setSearchParams] = useSearchParams();
    const viewState = searchParams.get("action") || "list";
    const editingId = searchParams.get("id") || null;
    const editType = searchParams.get("type") || "contact";

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ nazwa: "", value: "", link: "", icon: "", type: "contact" });

    const adresAPI = "https://api.pth-hokej.pl";

    const fetchData = async () => {
        try {
            const [cRes, sRes] = await Promise.all([
                fetch(`${adresAPI}/api/contacts`),
                fetch(`${adresAPI}/api/socials`)
            ]);
            setContacts(await cRes.json());
            setSocials(await sRes.json());
        } catch (err) { console.error(err); }
    };

    useEffect(() => { fetchData(); }, []);

    useEffect(() => {
        if (viewState === "edit" && editingId) {
            const sourceArray = editType === "contact" ? contacts : socials;
            const item = sourceArray.find(e => e._id === editingId);
            if (item) {
                setFormData({ 
                    nazwa: item.nazwa || "", 
                    value: item.value || "", 
                    link: item.link || "", 
                    icon: item.icon || "", 
                    type: editType 
                });
            }
        }
    }, [viewState, editingId, editType, contacts, socials]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const endpoint = formData.type === "contact" ? "contacts" : "socials";
            const url = viewState === "edit" ? `${adresAPI}/api/${endpoint}/${editingId}` : `${adresAPI}/api/${endpoint}`;
            const method = viewState === "edit" ? "PUT" : "POST";

            await fetch(url, { 
                method, 
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData) 
            });
            await fetchData();
            setSearchParams({});
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    const handleDelete = async (id, type) => {
        if (!window.confirm("Na pewno chcesz usunąć ten element?")) return;
        try {
            const endpoint = type === "contact" ? "contacts" : "socials";
            await fetch(`${adresAPI}/api/${endpoint}/${id}`, { method: "DELETE" });
            fetchData();
        } catch (err) { console.error(err); }
    };

    const handleAddClick = (type) => {
        setFormData({ nazwa: "", value: "", link: "", icon: "", type });
        setSearchParams({ action: "add", type });
    };

    const handleEditClick = (item, type) => {
        setFormData({ nazwa: item.nazwa || "", value: item.value || "", link: item.link || "", icon: item.icon || "", type });
        setSearchParams({ action: "edit", id: item._id, type });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <ManagerContainer>
            {viewState === "list" && (
                <>
                    {/* KONTAKTY */}
                    <HeaderRow>
                        <h2><Phone size={28} /> Dane Kontaktowe</h2>
                        <PrimaryButton onClick={() => handleAddClick('contact')}><Plus size={20} /> Dodaj Kontakt</PrimaryButton>
                    </HeaderRow>
                    {contacts.length === 0 ? <p style={{ color: "#666", marginBottom: '50px' }}>Brak danych kontaktowych.</p> : (
                        <Grid>
                            {contacts.map((item) => (
                                <ContactCard key={item._id}>
                                    <div className="content">
                                        <div className="icon-circle">
                                            {item.icon ? <img src={item.icon} alt="icon" /> : <Phone />}
                                        </div>
                                        <div className="info">
                                            <h3>{item.nazwa}</h3>
                                            <p>{item.value}</p>
                                            {item.link && <span>{item.link}</span>}
                                        </div>
                                    </div>
                                    <div className="actions">
                                        <button className="btn-edit" onClick={() => handleEditClick(item, 'contact')}><Edit size={16} /> Edytuj</button>
                                        <button className="btn-delete" onClick={() => handleDelete(item._id, 'contact')}><Trash2 size={16} /> Usuń</button>
                                    </div>
                                </ContactCard>
                            ))}
                        </Grid>
                    )}

                    {/* SOCIAL MEDIA */}
                    <HeaderRow style={{ marginTop: '40px', borderTop: '2px solid #eee', paddingTop: '40px' }}>
                        <h2><Share2 size={28} /> Social Media</h2>
                        <PrimaryButton onClick={() => handleAddClick('social')}><Plus size={20} /> Dodaj Social</PrimaryButton>
                    </HeaderRow>
                    {socials.length === 0 ? <p style={{ color: "#666" }}>Brak dodanych linków do social mediów.</p> : (
                        <Grid>
                            {socials.map((item) => (
                                <ContactCard key={item._id}>
                                    <div className="content">
                                        <div className="icon-circle">
                                            {item.icon ? <img src={item.icon} alt="icon" /> : <Share2 />}
                                        </div>
                                        <div className="info">
                                            <h3>{item.nazwa}</h3>
                                            <p>{item.value}</p>
                                            {item.link && <span>{item.link}</span>}
                                        </div>
                                    </div>
                                    <div className="actions">
                                        <button className="btn-edit" onClick={() => handleEditClick(item, 'social')}><Edit size={16} /> Edytuj</button>
                                        <button className="btn-delete" onClick={() => handleDelete(item._id, 'social')}><Trash2 size={16} /> Usuń</button>
                                    </div>
                                </ContactCard>
                            ))}
                        </Grid>
                    )}
                </>
            )}

            {(viewState === "add" || viewState === "edit") && (
                <>
                    <HeaderRow>
                        <SecondaryButton onClick={() => setSearchParams({})}><ChevronLeft size={20} /> Wróć do listy</SecondaryButton>
                        <h2>{viewState === "edit" ? "Edycja kafelka" : "Dodawanie kafelka"} ({formData.type === 'contact' ? 'Kontakt' : 'Social Media'})</h2>
                    </HeaderRow>

                    <FormCard onSubmit={handleSubmit}>
                        <FormSectionTitle><AlignLeft size={20}/> Informacje wyświetlane na stronie</FormSectionTitle>
                        
                        <FormGroupGrid>
                            <FormGroup>
                                <label>Nazwa (np. Telefon, Instagram)</label>
                                <input required name="nazwa" placeholder="np. Telefon" value={formData.nazwa} onChange={handleChange} />
                            </FormGroup>
                            <FormGroup>
                                <label>Wartość tekstowa (nr telefonu, email, nazwa konta)</label>
                                <input required name="value" placeholder="np. +48 123 456 789" value={formData.value} onChange={handleChange} />
                            </FormGroup>
                        </FormGroupGrid>

                        <FormSectionTitle style={{marginTop: '30px'}}><LinkIcon size={20}/> Akcja po kliknięciu i Wygląd</FormSectionTitle>
                        <FormGroupGrid>
                            <FormGroup>
                                <label>Link docelowy (opcjonalny)</label>
                                <input name="link" placeholder="np. mailto:adres@klub.pl, tel:+48123456789, https://..." value={formData.link} onChange={handleChange} />
                            </FormGroup>
                            <FormGroup>
                                <label>Ścieżka do ikony / URL (opcjonalne)</label>
                                <input name="icon" placeholder="np. /icons/phone.svg lub pełen link do obrazka" value={formData.icon} onChange={handleChange} />
                            </FormGroup>
                        </FormGroupGrid>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                            <PrimaryButton type="submit" disabled={loading}>
                                <Save size={20} /> {viewState === "edit" ? "Zapisz Zmiany" : "Zapisz Nowy Kafelek"}
                            </PrimaryButton>
                        </div>
                    </FormCard>
                </>
            )}
        </ManagerContainer>
    );
}